import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageChromeContext';
import { FiPlus, FiUser, FiMail, FiPhone, FiTrash2, FiX, FiSave, FiArrowUp, FiArrowDown, FiUsers, FiFilter } from 'react-icons/fi';
import { useListViewPreferences } from '../hooks/useListViewPreferences';
import ListColumnPicker from '../components/ListColumnPicker';
import ListSearchBar from '../components/ListSearchBar';
import ListPager from '../components/ListPager';
import { LIST_VIEW_KEYS, CANDIDATE_COLUMNS } from '../config/listViewColumns';
import { useBulkSelection } from '../hooks/useBulkSelection';
import LoadingButton from '../components/LoadingButton';
import BulkActionBar from '../components/BulkActionBar';
import IconButton from '../components/IconButton';
import Modal from '../components/Modal';
import { downloadCsv } from '../utils/exportCsv';
import { summarizeAiMatchResults } from '../utils/aiMatchMessages';
import { isActiveStatus } from '../utils/activeStatus';
import './Candidates.css';
import { timestampSortValue } from '../utils/formatTimestamp';
import { appAlert, appConfirm } from '../utils/appDialog';
import { useAtsMatchRun } from '../context/AtsMatchRunContext';
import { extractTextFromFile } from '../utils/resumeExtract';
import SendTemplateEmailModal from '../components/SendTemplateEmailModal';
import { useClientPager } from '../hooks/useClientPager';

async function appendResumeUploadFields(formData, resume) {
  formData.append('resume', resume.file);
  formData.append(
    'skills',
    JSON.stringify(
      resume.skills
        ? resume.skills
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s)
        : []
    )
  );
  formData.append('experience_years', resume.experience_years || '');
  formData.append('education', resume.education || '');
  formData.append('summary', resume.summary || '');
  formData.append('autofill_profile', 'true');
  try {
    const text = await extractTextFromFile(resume.file);
    if (text?.trim()) formData.append('content_text', text.trim());
  } catch (e) {
    console.warn('Resume text extract skipped:', e.message);
  }
  return formData;
}

const CANDIDATE_EXPORT_COLUMNS = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'current_job_title', label: 'Current Job Title' },
  { key: 'current_company', label: 'Current Company' },
  { key: 'years_of_experience', label: 'Years of Experience' },
  { key: 'resume_count', label: 'Resume Count' },
  { key: 'match_count', label: 'Match Count' },
  {
    key: 'is_active',
    label: 'Status',
    format: (row) => (isActiveStatus(row.is_active) ? 'Active' : 'Inactive'),
  },
  {
    key: 'package_status',
    label: 'Package',
    format: (row) => (row.package_status === 'Active' ? 'Active' : 'Inactive'),
  },
  {
    key: 'active_package_name',
    label: 'Active Package Name',
  },
];

/** Map register_candidates row → candidate form defaults (fname/lname/email/phone/position/service/start_date/price/ref). */
function mapRegisterRowToCandidateForm(row) {
  if (!row) return null;
  const fname = row.fname ?? row.first_name ?? '';
  const lname = row.lname ?? row.last_name ?? '';
  const email = row.email ?? '';
  const phone = row.phone ?? '';
  const position = row.position ?? row.current_job_title ?? '';
  const service = row.service ?? '';
  const start = row.start_date ?? '';
  const price = row.price;
  const ref = row.ref ?? '';
  const noteLines = [];
  if (service) noteLines.push(`Register service: ${service}`);
  if (start) noteLines.push(`Register start date: ${start}`);
  if (price !== undefined && price !== null && price !== '') noteLines.push(`Register rate/price: ${price}`);
  if (ref) noteLines.push(`Register ref: ${ref}`);
  const additional_notes = noteLines.join('\n');
  let expected_salary_min = '';
  let expected_salary_max = '';
  const p = typeof price === 'number' ? price : parseFloat(String(price).replace(/[^0-9.-]/g, ''));
  if (!Number.isNaN(p) && String(price).trim() !== '') {
    expected_salary_min = String(Math.floor(p));
    expected_salary_max = String(Math.ceil(p));
  }
  return {
    email: String(email).trim(),
    password: '',
    first_name: String(fname).trim(),
    last_name: String(lname).trim(),
    phone: String(phone).trim(),
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    linkedin_url: '',
    portfolio_url: '',
    github_url: '',
    job_classification: '',
    current_job_title: String(position).trim(),
    current_company: '',
    years_of_experience: '',
    availability: 'available',
    expected_salary_min,
    expected_salary_max,
    work_authorization: '',
    willing_to_relocate: false,
    preferred_locations: '',
    summary: '',
    additional_notes,
  };
}

const Candidates = () => {
  const { user } = useAuth();
  usePageTitle(user?.role === 'admin' ? 'All Candidates' : 'Assigned Candidates');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { startCandidateMatch } = useAtsMatchRun();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRegisterSelectModal, setShowRegisterSelectModal] = useState(false);
  const [addCandidateSource, setAddCandidateSource] = useState(null); // 'manual' | 'register' | null
  const [selectedRegisterRow, setSelectedRegisterRow] = useState(null);
  const [registerPickerSearch, setRegisterPickerSearch] = useState('');
  const [registerPickerSelectedId, setRegisterPickerSelectedId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showSaveKpiModal, setShowSaveKpiModal] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [kpiName, setKpiName] = useState('');
  const [quickSearch, setQuickSearch] = useState('');
  
  // Sort state
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Dynamic filter state - array of filter conditions
  const [filterConditions, setFilterConditions] = useState([]);
  // Local state for input values (to prevent re-renders on every keystroke)
  const [localFilterValues, setLocalFilterValues] = useState({});
  
  // Available filter fields with their types
  const filterFields = [
    { value: 'first_name', label: 'First Name', type: 'text' },
    { value: 'last_name', label: 'Last Name', type: 'text' },
    { value: 'email', label: 'Email', type: 'text' },
    { value: 'phone', label: 'Phone', type: 'text' },
    { value: 'city', label: 'City', type: 'text' },
    { value: 'state', label: 'State', type: 'text' },
    { value: 'country', label: 'Country', type: 'text' },
    { value: 'current_job_title', label: 'Job Title', type: 'text' },
    { value: 'current_company', label: 'Company', type: 'text' },
    { value: 'years_of_experience', label: 'Years of Experience', type: 'number' },
    { value: 'availability', label: 'Availability', type: 'select', options: ['available', 'not-available', 'available-soon', 'contract-only'] },
    { value: 'work_authorization', label: 'Work Authorization', type: 'text' },
    { value: 'willing_to_relocate', label: 'Willing to Relocate', type: 'boolean' },
    { value: 'is_active', label: 'Status', type: 'boolean' },
  ];
  const [candidateFormData, setCandidateFormData] = useState({
    // User fields
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    // Profile fields
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    linkedin_url: '',
    portfolio_url: '',
    github_url: '',
      current_job_title: '',
    current_company: '',
    years_of_experience: '',
    availability: 'available',
    expected_salary_min: '',
    expected_salary_max: '',
    work_authorization: '',
    willing_to_relocate: false,
    preferred_locations: '',
    summary: '',
    additional_notes: '',
  });
  const [resumeFiles, setResumeFiles] = useState([]);
  const [resumeData, setResumeData] = useState([
    { skills: '', experience_years: '', education: '', summary: '' },
    { skills: '', experience_years: '', education: '', summary: '' },
    { skills: '', experience_years: '', education: '', summary: '' },
  ]);

  // Track if we've initialized from URL params
  const initializedFromUrl = useRef(false);

  // Initialize filters from URL params (only on mount)
  useEffect(() => {
    if (initializedFromUrl.current) return;
    initializedFromUrl.current = true;
    
    const queryParam = searchParams.get('query');
    if (queryParam) {
      try {
        const conditions = JSON.parse(decodeURIComponent(queryParam));
        setFilterConditions(conditions);
        setLocalFilterValues({}); // Clear any local values when initializing from URL
      } catch (e) {
        console.error('Error parsing filter conditions from URL:', e);
      }
    }
  }, [searchParams]);

  // Parse query syntax (e.g., "name=test", "role like Data%", "year>1")
  const parseQueryValue = (value) => {
    if (!value || !value.trim()) return null;
    
    const trimmed = value.trim();
    
    // Check for LIKE pattern first (before comparison operators): "like Data%"
    const likeMatch = trimmed.match(/^like\s+(.+)$/i);
    if (likeMatch) {
      return {
        operator: 'like',
        value: likeMatch[1].trim()
      };
    }
    
    // Check for exact match: "=value"
    const exactMatch = trimmed.match(/^=\s*(.+)$/);
    if (exactMatch) {
      return {
        operator: '=',
        value: exactMatch[1].trim()
      };
    }
    
    // Check for comparison operators at the start: ">1", "<5", ">=10", "<=20"
    const comparisonMatch = trimmed.match(/^(>=|<=|>|<)\s*(.+)$/);
    if (comparisonMatch) {
      return {
        operator: comparisonMatch[1],
        value: comparisonMatch[2].trim()
      };
    }
    
    // Default: LIKE for text fields (contains search)
    return {
      operator: 'like', // Default to LIKE for text fields
      value: trimmed
    };
  };

  // Debounced filter params for API calls (prevents refetch on every keystroke)
  const [debouncedFilterParams, setDebouncedFilterParams] = useState('');
  const filterParamsTimeout = useRef(null);

  useEffect(() => {
    // Clear existing timeout
    if (filterParamsTimeout.current) {
      clearTimeout(filterParamsTimeout.current);
    }
    
    // Build filter params
    const params = new URLSearchParams();
    if (filterConditions.length > 0 && filterConditions.some(f => f.field && f.value)) {
      params.append('query', JSON.stringify(filterConditions));
    }
    if (quickSearch.trim()) {
      params.append('search', quickSearch.trim());
    }
    const paramsString = params.toString();
    
    // Debounce the API call by 500ms
    filterParamsTimeout.current = setTimeout(() => {
      setDebouncedFilterParams(paramsString);
    }, 500);
    
    return () => {
      if (filterParamsTimeout.current) {
        clearTimeout(filterParamsTimeout.current);
      }
    };
  }, [filterConditions, quickSearch]);

  // Get candidates based on role with filters
  const { data: candidates, isLoading } = useQuery(
    ['candidates', user?.role, debouncedFilterParams],
    () => {
      const endpoint = user?.role === 'admin' ? '/candidates' : '/candidates/assigned';
      const url = debouncedFilterParams ? `${endpoint}?${debouncedFilterParams}` : endpoint;
      return api.get(url).then(res => res.data);
    },
    {
      enabled: Boolean(user?.id && user?.role),
      refetchOnWindowFocus: false,
      staleTime: 30000 // Consider data fresh for 30 seconds
    }
  );

  // Fetch job roles for dropdown
  const { data: jobRoles = [] } = useQuery(
    ['job-roles'],
    () => api.get('/job-roles').then(res => res.data),
    {
      enabled: showAddModal || showEditModal
    }
  );

  const { data: registerRows = [], isLoading: registerRowsLoading, isError: registerRowsError } = useQuery(
    ['register-candidates', 'picker'],
    () => api.get('/register-candidates?exclude_onboarded=1').then((res) => res.data),
    {
      enabled: user?.role === 'admin' && showRegisterSelectModal,
      refetchOnWindowFocus: false,
    }
  );

  const filteredRegisterRows = useMemo(() => {
    const q = registerPickerSearch.trim().toLowerCase();
    if (!q) return registerRows;
    return registerRows.filter((r) => {
      const parts = [r.fname, r.lname, r.first_name, r.last_name, r.email, r.phone, r.position, r.service, r.ref]
        .filter(Boolean)
        .map((v) => String(v));
      return parts.some((p) => p.toLowerCase().includes(q));
    });
  }, [registerRows, registerPickerSearch]);

  // Update candidate status mutation (admin only)
  const updateStatusMutation = useMutation(
    ({ candidateId, is_active }) => api.put(`/candidates/${candidateId}/status`, { is_active }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['candidates', user?.role]);
      },
    }
  );

  const {
    colIndex,
    toggleColumn,
    moveColumn,
    resetColumns,
    visibleColumnIds,
    setVisibleColumnIds,
    isLoading: prefsLoading,
    allColumns,
    getColumnProps,
    ResizeHandle,
    tableRef,
  } = useListViewPreferences(LIST_VIEW_KEYS.CANDIDATES, CANDIDATE_COLUMNS);

  const packageColumnInjected = useRef(false);
  useEffect(() => {
    if (prefsLoading || packageColumnInjected.current) return;
    packageColumnInjected.current = true;
    if (visibleColumnIds.includes('package')) return;
    const next = [...visibleColumnIds];
    const statusIdx = next.indexOf('status');
    if (statusIdx >= 0) next.splice(statusIdx, 0, 'package');
    else next.push('package');
    setVisibleColumnIds(next);
  }, [prefsLoading, visibleColumnIds, setVisibleColumnIds]);

  // Debounce URL updates to prevent page refresh on every keystroke
  const isInitialMount = useRef(true);
  const urlUpdateTimeout = useRef(null);
  
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    // Clear existing timeout
    if (urlUpdateTimeout.current) {
      clearTimeout(urlUpdateTimeout.current);
    }
    
    // Debounce URL update by 500ms
    urlUpdateTimeout.current = setTimeout(() => {
      const newParams = new URLSearchParams();
      if (filterConditions.length > 0 && filterConditions.some(f => f.field && f.value)) {
        newParams.set('query', JSON.stringify(filterConditions));
      }
      setSearchParams(newParams, { replace: true });
    }, 500);
    
    // Cleanup timeout on unmount
    return () => {
      if (urlUpdateTimeout.current) {
        clearTimeout(urlUpdateTimeout.current);
      }
    };
  }, [filterConditions, setSearchParams]);

  const handleAddFilter = () => {
    setFilterConditions(prev => [...prev, { field: '', value: '', operator: 'like' }]);
  };

  const handleRemoveFilter = (index) => {
    // Clean up local value for this filter
    const inputKey = `filter-${index}-value`;
    if (filterValueTimeout.current[inputKey]) {
      clearTimeout(filterValueTimeout.current[inputKey]);
      delete filterValueTimeout.current[inputKey];
    }
    setLocalFilterValues(prev => {
      const newVals = { ...prev };
      delete newVals[inputKey];
      return newVals;
    });
    setFilterConditions(prev => prev.filter((_, i) => i !== index));
  };

  // Debounce timer for filter value updates
  const filterValueTimeout = useRef({});
  
  const handleFilterChange = (index, field, value) => {
    // For value field, use local state and debounce the update
    if (field === 'value') {
      const inputKey = `filter-${index}-value`;
      setLocalFilterValues(prev => ({ ...prev, [inputKey]: value }));
      
      // Clear existing timeout for this input
      if (filterValueTimeout.current[inputKey]) {
        clearTimeout(filterValueTimeout.current[inputKey]);
      }
      
      // Debounce the actual filter condition update
      filterValueTimeout.current[inputKey] = setTimeout(() => {
        setFilterConditions(prev => {
          const updated = [...prev];
          const fieldDef = filterFields.find(f => f.value === updated[index].field);
          
          if (fieldDef && fieldDef.type === 'text') {
            const parsed = parseQueryValue(value);
            if (parsed) {
              updated[index] = { 
                ...updated[index], 
                operator: parsed.operator,
                value: parsed.value
              };
            } else {
              updated[index] = { ...updated[index], value };
            }
          } else {
            updated[index] = { ...updated[index], value };
          }
          
          return updated;
        });
      }, 300);
    } else {
      // For non-value fields (field selection, operator), update immediately
      setFilterConditions(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], [field]: value };
        
        // Reset value when field changes
        if (field === 'field') {
          updated[index].value = '';
          updated[index].operator = 'like';
          // Clear local value for this input
          const inputKey = `filter-${index}-value`;
          setLocalFilterValues(prev => {
            const newVals = { ...prev };
            delete newVals[inputKey];
            return newVals;
          });
        }
        
        return updated;
      });
    }
  };
  
  // Get local value for input (or use actual value if no local value)
  const getFilterValue = (index, actualValue) => {
    const inputKey = `filter-${index}-value`;
    return localFilterValues[inputKey] !== undefined ? localFilterValues[inputKey] : (actualValue || '');
  };

  const handleClearFilters = () => {
    // Clean up all timeout refs
    Object.keys(filterValueTimeout.current).forEach(key => {
      if (filterValueTimeout.current[key]) {
        clearTimeout(filterValueTimeout.current[key]);
      }
    });
    filterValueTimeout.current = {};
    setLocalFilterValues({});
    setFilterConditions([]);
    setQuickSearch('');
    setSearchParams({}, { replace: true });
  };

  const hasActiveFilters =
    Boolean(quickSearch.trim()) ||
    (filterConditions.length > 0 && filterConditions.some((f) => f.field && f.value));

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort candidates
  const sortedCandidates = useMemo(() => {
    if (!candidates || !sortColumn) return candidates;
    
    return [...candidates].sort((a, b) => {
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      
      // Handle nested properties
      if (sortColumn === 'name') {
        aValue = `${a.first_name || ''} ${a.last_name || ''}`.trim();
        bValue = `${b.first_name || ''} ${b.last_name || ''}`.trim();
      } else if (sortColumn === 'current_position') {
        aValue = a.current_job_title || '';
        bValue = b.current_job_title || '';
      } else if (sortColumn === 'experience') {
        aValue = a.years_of_experience || 0;
        bValue = b.years_of_experience || 0;
      } else if (sortColumn === 'resumes') {
        aValue = a.resume_count || 0;
        bValue = b.resume_count || 0;
      } else if (sortColumn === 'matches') {
        aValue = a.match_count || 0;
        bValue = b.match_count || 0;
      } else if (sortColumn === 'package') {
        aValue = a.package_status === 'Active' ? 'Active' : 'Inactive';
        bValue = b.package_status === 'Active' ? 'Active' : 'Inactive';
      } else if (sortColumn === 'status') {
        aValue = isActiveStatus(a.is_active) ? 'Active' : 'Inactive';
        bValue = isActiveStatus(b.is_active) ? 'Active' : 'Inactive';
      }
      
      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      
      // Compare values
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue), undefined, { sensitivity: 'base' });
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [candidates, sortColumn, sortDirection]);

  const isStaff = user?.role === 'consultant' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';
  const {
    pageItems: pagedCandidates,
    pagination: candidatePagination,
    setPage: setCandidatePage,
  } = useClientPager(sortedCandidates || [], {
    resetKey: `${sortColumn}-${sortDirection}-${debouncedFilterParams || ''}`,
  });
  const candidateList = pagedCandidates;

  const {
    selectedItems,
    selectedCount,
    allSelected,
    someSelected,
    toggleOne,
    toggleAll,
    clearSelection,
    isSelected,
  } = useBulkSelection(candidateList);

  const handleBulkExport = () => {
    downloadCsv(selectedItems, CANDIDATE_EXPORT_COLUMNS, `candidates-export-${Date.now()}.csv`);
  };

  const handleBulkDelete = async () => {
    if (!await appConfirm(`Deactivate ${selectedCount} candidate(s)? They will be soft-deleted (marked inactive).`)) return;
    setBulkBusy(true);
    setBulkAction('delete');
    try {
      await Promise.all(
        selectedItems.map((candidate) =>
          api.put(`/candidates/${candidate.id}/status`, { is_active: false })
        )
      );
      queryClient.invalidateQueries(['candidates', user?.role]);
      clearSelection();
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setBulkBusy(false);
      setBulkAction(null);
    }
  };

  const handleBulkMatch = async () => {
    if (!await appConfirm(`Run AI matching for ${selectedCount} candidate(s) against all active jobs?`)) return;
    setBulkBusy(true);
    setBulkAction('match');
    try {
      const results = await Promise.all(
        selectedItems.map((candidate) =>
          startCandidateMatch(candidate.id, {
            label: [candidate.first_name, candidate.last_name].filter(Boolean).join(' '),
            notify: false,
          }).then((res) => res.data)
        )
      );
      void appAlert(summarizeAiMatchResults(results));
      clearSelection();
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setBulkBusy(false);
      setBulkAction(null);
    }
  };

  const handleSaveAsKpi = () => {
    if (!kpiName.trim()) {
      void appAlert('Please enter a name for the KPI');
      return;
    }
    // Save KPI with filter configuration
    const kpiData = {
      name: kpiName,
      description: `Filtered candidates: ${filterConditions.filter(f => f.field && f.value).map(f => `${f.field}=${f.value}`).join(', ')}`,
      metric_type: 'custom_filter',
      display_order: 0,
      query_config: { type: 'candidate_filter', conditions: filterConditions },
    };
    
    api.post('/kpis', kpiData)
      .then(() => {
        setShowSaveKpiModal(false);
        setKpiName('');
        void appAlert('KPI saved successfully!');
      })
      .catch(err => {
        console.error('Error saving KPI:', err);
        void appAlert('Failed to save KPI');
      });
  };

  const updateCandidateMutation = useMutation(
    async ({ candidateId, candidateData, resumes, existingResumeIds }) => {
      // Update the user
      await api.put(`/users/${candidateId}`, {
        email: candidateData.email,
        first_name: candidateData.first_name,
        last_name: candidateData.last_name,
        role: 'candidate',
        phone: candidateData.phone,
      });

      // Update password if provided
      if (candidateData.password) {
        await api.put(`/users/${candidateId}/password`, {
          password: candidateData.password,
        });
      }

      const userId = candidateId;

      // Update or create the profile
      const profileData = {
        user_id: userId,
        date_of_birth: candidateData.date_of_birth || null,
        address: candidateData.address || null,
        city: candidateData.city || null,
        state: candidateData.state || null,
        country: candidateData.country || null,
        zip_code: candidateData.zip_code || null,
        linkedin_url: candidateData.linkedin_url || null,
        portfolio_url: candidateData.portfolio_url || null,
        github_url: candidateData.github_url || null,
        job_classification: candidateData.job_classification ? parseInt(candidateData.job_classification) : null,
        current_job_title: candidateData.current_job_title || null,
        current_company: candidateData.current_company || null,
        years_of_experience: candidateData.years_of_experience ? parseInt(candidateData.years_of_experience) : null,
        availability: candidateData.availability || null,
        expected_salary_min: candidateData.expected_salary_min ? parseInt(candidateData.expected_salary_min) : null,
        expected_salary_max: candidateData.expected_salary_max ? parseInt(candidateData.expected_salary_max) : null,
        work_authorization: candidateData.work_authorization || null,
        willing_to_relocate: candidateData.willing_to_relocate || false,
        preferred_locations: candidateData.preferred_locations ? candidateData.preferred_locations.split(',').map(l => l.trim()).filter(l => l) : [],
        summary: candidateData.summary || null,
        additional_notes: candidateData.additional_notes || null,
      };

      // The endpoint handles both create and update
      await api.post('/candidate-profiles', profileData);

      // Handle resumes
      for (let i = 0; i < resumes.length; i++) {
        const resume = resumes[i];
        
        if (resume.id && resume.file) {
          // Replace existing resume: delete old one and upload new one
          await api.delete(`/resumes/${resume.id}`);
          const formData = new FormData();
          await appendResumeUploadFields(formData, resume);
          await api.post(`/resumes/upload-for-candidate/${userId}`, formData);
        } else if (resume.id && !resume.file) {
          // Update existing resume metadata only
          await api.put(`/resumes/${resume.id}`, {
            skills: resume.skills ? resume.skills.split(',').map(s => s.trim()).filter(s => s) : [],
            experience_years: resume.experience_years ? parseInt(resume.experience_years) : null,
            education: resume.education || '',
            summary: resume.summary || '',
          });
        } else if (!resume.id && resume.file) {
          // Upload new resume
          const formData = new FormData();
          await appendResumeUploadFields(formData, resume);
          await api.post(`/resumes/upload-for-candidate/${userId}`, formData);
        }
      }

      return { id: userId };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('candidates');
        setShowEditModal(false);
        setEditingCandidate(null);
        resetForm();
      },
    }
  );

  const createCandidateMutation = useMutation(
    async ({ candidateData, resumes }) => {
      // First create the user
      const userResponse = await api.post('/users', {
        email: candidateData.email,
        password: candidateData.password,
        first_name: candidateData.first_name,
        last_name: candidateData.last_name,
        role: 'candidate',
        phone: candidateData.phone,
      });

      const userId = userResponse.data.id;

      // Then create the profile if there's any profile data
      const profileData = {
        user_id: userId,
        date_of_birth: candidateData.date_of_birth || null,
        address: candidateData.address || null,
        city: candidateData.city || null,
        state: candidateData.state || null,
        country: candidateData.country || null,
        zip_code: candidateData.zip_code || null,
        linkedin_url: candidateData.linkedin_url || null,
        portfolio_url: candidateData.portfolio_url || null,
        github_url: candidateData.github_url || null,
        job_classification: candidateData.job_classification ? parseInt(candidateData.job_classification) : null,
        current_job_title: candidateData.current_job_title || null,
        current_company: candidateData.current_company || null,
        years_of_experience: candidateData.years_of_experience ? parseInt(candidateData.years_of_experience) : null,
        availability: candidateData.availability || null,
        expected_salary_min: candidateData.expected_salary_min ? parseInt(candidateData.expected_salary_min) : null,
        expected_salary_max: candidateData.expected_salary_max ? parseInt(candidateData.expected_salary_max) : null,
        work_authorization: candidateData.work_authorization || null,
        willing_to_relocate: candidateData.willing_to_relocate || false,
        preferred_locations: candidateData.preferred_locations ? candidateData.preferred_locations.split(',').map(l => l.trim()).filter(l => l) : [],
        summary: candidateData.summary || null,
        additional_notes: candidateData.additional_notes || null,
      };

      // Only create profile if there's at least one field filled
      const hasProfileData = Object.values(profileData).some((val, idx) => {
        if (idx === 0) return false; // Skip user_id
        return val !== null && val !== false && val !== '';
      });

      if (hasProfileData) {
        await api.post('/candidate-profiles', profileData);
      }

      // Upload resumes if any
      if (resumes && resumes.length > 0) {
        for (let i = 0; i < resumes.length; i++) {
          const resume = resumes[i];
          if (resume.file) {
            const formData = new FormData();
            await appendResumeUploadFields(formData, resume);
            await api.post(`/resumes/upload-for-candidate/${userId}`, formData);
          }
        }
      }

      return userResponse.data;
    },
    {
      onSuccess: async (createdUser, variables) => {
        queryClient.invalidateQueries('candidates');
        queryClient.invalidateQueries(['register-candidates', 'picker']);
        queryClient.invalidateQueries(['register-candidates']);
        // When onboarded from Register, mark status=created to avoid duplicates
        const regId = variables?.registerId || selectedRegisterRow?.id;
        if (regId && createdUser?.id) {
          try {
            await api.post(`/register-candidates/${regId}/mark-created`, {
              created_user_id: createdUser.id,
            });
            queryClient.invalidateQueries(['register-candidates']);
            queryClient.invalidateQueries(['register-candidates', 'picker']);
          } catch (e) {
            console.warn('Failed to mark register row created:', e);
          }
        }
        setShowAddModal(false);
        setAddCandidateSource(null);
        setSelectedRegisterRow(null);
        resetForm();
      },
    }
  );

  const resetForm = () => {
    setCandidateFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      date_of_birth: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zip_code: '',
      linkedin_url: '',
      portfolio_url: '',
      github_url: '',
      job_classification: '',
      current_job_title: '',
      current_company: '',
      years_of_experience: '',
      availability: 'available',
      expected_salary_min: '',
      expected_salary_max: '',
      work_authorization: '',
      willing_to_relocate: false,
      preferred_locations: '',
      summary: '',
      additional_notes: '',
    });
    setResumeFiles([]);
    setResumeData([
      { skills: '', experience_years: '', education: '', summary: '' },
      { skills: '', experience_years: '', education: '', summary: '' },
      { skills: '', experience_years: '', education: '', summary: '' },
    ]);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddCandidateSource(null);
    setSelectedRegisterRow(null);
    resetForm();
  };

  /** Open the create form directly (no method chooser step). */
  const openAddCandidate = () => {
    resetForm();
    setAddCandidateSource('manual');
    setSelectedRegisterRow(null);
    setShowRegisterSelectModal(false);
    setShowAddModal(true);
  };

  const openOnboardFromRegister = () => {
    setRegisterPickerSearch('');
    setRegisterPickerSelectedId(null);
    setShowAddModal(false);
    setShowRegisterSelectModal(true);
  };

  const closeRegisterSelectModal = () => {
    setShowRegisterSelectModal(false);
    // Return to the direct add form rather than a method chooser.
    if (!showAddModal) {
      openAddCandidate();
    }
  };

  const handleContinueFromRegisterSelection = () => {
    const row = registerRows.find((r) => String(r.id) === String(registerPickerSelectedId));
    if (!row) return;
    const mapped = mapRegisterRowToCandidateForm(row);
    setCandidateFormData(mapped);
    setResumeFiles([null, null, null]);
    setResumeData([
      { skills: '', experience_years: '', education: '', summary: '' },
      { skills: '', experience_years: '', education: '', summary: '' },
      { skills: '', experience_years: '', education: '', summary: '' },
    ]);
    setAddCandidateSource('register');
    setSelectedRegisterRow(row);
    setShowRegisterSelectModal(false);
    setShowAddModal(true);
  };

  const handleAddCandidate = (e) => {
    e.preventDefault();
    
    // Prepare resume data - only include resumes with files
    const resumes = resumeFiles
      .map((file, index) => file ? {
        file: file,
        ...resumeData[index],
      } : null)
      .filter(resume => resume !== null);

    createCandidateMutation.mutate({
      candidateData: candidateFormData,
      resumes: resumes,
      registerId: selectedRegisterRow?.id || null,
    });
  };

  const handleUpdateCandidate = (e) => {
    e.preventDefault();
    
    // Prepare resume data
    const resumes = resumeFiles.map((file, index) => {
      const data = resumeData[index];
      return {
        id: data.id, // Existing resume ID if editing
        file: file, // New file if replacing
        ...data,
      };
    }).filter(resume => resume.file || resume.id); // Include if has file or is existing

    const existingResumeIds = resumes.filter(r => r.id && !r.file).map(r => r.id);

    updateCandidateMutation.mutate({
      candidateId: editingCandidate.id,
      candidateData: candidateFormData,
      resumes: resumes,
      existingResumeIds: existingResumeIds,
    });
  };

  const handleResumeFileChange = (index, file) => {
    const newFiles = [...resumeFiles];
    newFiles[index] = file;
    setResumeFiles(newFiles);
  };

  const handleResumeDataChange = (index, field, value) => {
    const newResumeData = [...resumeData];
    newResumeData[index] = {
      ...newResumeData[index],
      [field]: value,
    };
    setResumeData(newResumeData);
  };

  const removeResume = (index) => {
    const newFiles = [...resumeFiles];
    const newResumeData = [...resumeData];
    newFiles[index] = null;
    newResumeData[index] = { skills: '', experience_years: '', education: '', summary: '' };
    setResumeFiles(newFiles);
    setResumeData(newResumeData);
  };

  const handleCandidateClick = (candidate) => {
    navigate(`/candidates/${candidate.id}`);
  };

  if (isLoading && candidates == null) {
    return <div className="loading">Loading candidates...</div>;
  }

  return (
    <div className="candidates-page list-page">
      <div className={`page-header${selectedCount > 0 ? ' page-header--bulk' : ''}`}>
        {selectedCount > 0 ? (
          <BulkActionBar
            className="bulk-action-bar--header"
            count={selectedCount}
            onClear={clearSelection}
            onExport={handleBulkExport}
            onDelete={isAdmin ? handleBulkDelete : undefined}
            onMatch={isStaff ? handleBulkMatch : undefined}
            showDelete={isAdmin}
            showMatch={isStaff}
            busy={bulkBusy}
            busyAction={bulkAction}
          />
        ) : (
          <h1>{user?.role === 'admin' ? 'All Candidates' : 'Assigned Candidates'}</h1>
        )}
        <div className="list-page-header-actions page-toolbar">
          <ListColumnPicker
            allColumns={allColumns}
            visibleColumnIds={visibleColumnIds}
            onToggle={toggleColumn}
            onMove={moveColumn}
            onReset={resetColumns}
          />
          {filterConditions.length > 0 && filterConditions.some((f) => f.field && f.value) && (
            <IconButton
              icon={FiSave}
              label="Save as KPI"
              variant="primary"
              onClick={() => setShowSaveKpiModal(true)}
            />
          )}
          {user?.role === 'admin' && (
            <IconButton
              icon={FiPlus}
              label="Add candidate"
              variant="primary"
              onClick={openAddCandidate}
            />
          )}
        </div>
      </div>

      <div className="list-filters-panel list-toolbar-panel compact-filters-bar">
        <div className="filter-row list-toolbar-row">
          <div className="filter-group filter-group--wide list-toolbar-search">
            <ListSearchBar
              id="candidates-quick-search"
              value={quickSearch}
              onChange={setQuickSearch}
              placeholder="Search name, email, company…"
            />
          </div>
          <IconButton
            icon={FiFilter}
            label={showFilters ? 'Hide filters' : 'More filters'}
            active={showFilters || (filterConditions.length > 0 && filterConditions.some((f) => f.field && f.value))}
            onClick={() => setShowFilters(!showFilters)}
          />
          {hasActiveFilters && (
            <IconButton icon={FiX} label="Clear filters" onClick={handleClearFilters} />
          )}
          {hasActiveFilters && (
            <span className="compact-filters-info">
              {sortedCandidates?.length || 0} result(s)
            </span>
          )}
        </div>
        {showFilters && (
        <div className="compact-filters-content">
          {filterConditions.length === 0 ? (
            <div className="compact-filter-row compact-filter-row--empty">
              <span className="compact-filters-empty-label">Add field filters</span>
              <button
                type="button"
                className="btn btn-sm btn-primary compact-filter-add"
                onClick={handleAddFilter}
              >
                <FiPlus /> Add filter
              </button>
              <select
                className="compact-filter-field compact-filter-quick"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    const fieldDef = filterFields.find(f => f.value === e.target.value);
                    setFilterConditions([{
                      field: e.target.value,
                      value: '',
                      operator: fieldDef?.type === 'number' ? '=' : 'like',
                    }]);
                  }
                }}
              >
                <option value="">Quick: pick a field…</option>
                {filterFields.map(field => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="compact-filter-conditions">
              {filterConditions.map((condition, index) => {
                const fieldDef = filterFields.find(f => f.value === condition.field);
                return (
                  <div key={index} className="compact-filter-row">
                    <select
                      className="compact-filter-field"
                      value={condition.field}
                      onChange={(e) => handleFilterChange(index, 'field', e.target.value)}
                    >
                      <option value="">Select field</option>
                      {filterFields.map(field => (
                        <option key={field.value} value={field.value}>{field.label}</option>
                      ))}
                    </select>
                    
                    {fieldDef && (
                      <>
                        {fieldDef.type === 'number' && (
                          <select
                            className="compact-filter-operator"
                            value={condition.operator || '='}
                            onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
                          >
                            <option value="=">=</option>
                            <option value=">">&gt;</option>
                            <option value="<">&lt;</option>
                            <option value=">=">&gt;=</option>
                            <option value="<=">&lt;=</option>
                          </select>
                        )}
                        {fieldDef.type === 'select' ? (
                          <select
                            className="compact-filter-value"
                            value={condition.value}
                            onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                          >
                            <option value="">All</option>
                            {fieldDef.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : fieldDef.type === 'boolean' ? (
                          <select
                            className="compact-filter-value"
                            value={condition.value}
                            onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                          >
                            <option value="">All</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : (
                          <input
                            type={fieldDef.type === 'number' ? 'number' : 'text'}
                            className="compact-filter-value"
                            placeholder={fieldDef.type === 'number' ? 'Value' : 'Value (e.g., test, =exact, like Data%)'}
                            value={getFilterValue(index, condition.value)}
                            onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                          />
                        )}
                      </>
                    )}
                    <button
                      type="button"
                      className="btn-icon btn-delete compact-filter-remove"
                      onClick={() => handleRemoveFilter(index)}
                      title="Remove filter"
                    >
                      <FiX />
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-sm btn-primary compact-filter-add"
                onClick={handleAddFilter}
              >
                <FiPlus /> Add filter
              </button>
              {filterConditions.length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary compact-filter-clear"
                  onClick={() => {
                    Object.keys(filterValueTimeout.current).forEach((key) => {
                      if (filterValueTimeout.current[key]) {
                        clearTimeout(filterValueTimeout.current[key]);
                      }
                    });
                    filterValueTimeout.current = {};
                    setLocalFilterValues({});
                    setFilterConditions([]);
                  }}
                >
                  Clear field filters
                </button>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Save as KPI Modal */}
      {showSaveKpiModal && (
        <div className="modal-overlay" onClick={() => setShowSaveKpiModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Save Filter as KPI</h2>
            <div className="form-group">
              <label>KPI Name</label>
              <input
                type="text"
                placeholder="e.g., Available Candidates in SF"
                value={kpiName}
                onChange={(e) => setKpiName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowSaveKpiModal(false);
                  setKpiName('');
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveAsKpi}
              >
                Save KPI
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="candidates-table-container">
        <table ref={tableRef} className="table candidates-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              {visibleColumnIds.map((columnId) => {
                const idx = colIndex(columnId);
                if (columnId === 'select') {
                  return (
                    <th key={columnId} {...getColumnProps(idx)} className="list-select-cell">
                      <input
                        type="checkbox"
                        aria-label="Select all candidates"
                        checked={allSelected && candidateList.length > 0}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={toggleAll}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  );
                }
                const sortable = ['name', 'email', 'phone', 'current_position', 'experience', 'resumes', 'matches', 'package', 'status'].includes(columnId);
                const labels = {
                  name: 'Name',
                  email: 'Email',
                  phone: 'Phone',
                  current_position: 'Current Position',
                  experience: 'Experience',
                  resumes: 'Resumes',
                  matches: 'Matches',
                  package: 'Package',
                  status: 'Status',
                };
                return (
                  <th
                    key={columnId}
                    {...getColumnProps(idx)}
                    className={sortable ? 'sortable' : undefined}
                    onClick={sortable ? () => handleSort(columnId) : undefined}
                    style={{
                      ...getColumnProps(idx).style,
                      cursor: sortable ? 'pointer' : undefined,
                    }}
                  >
                    {labels[columnId] || columnId}
                    {sortable && sortColumn === columnId && (
                      sortDirection === 'asc'
                        ? <FiArrowUp className="list-sort-icon" aria-hidden />
                        : <FiArrowDown className="list-sort-icon" aria-hidden />
                    )}
                    <ResizeHandle index={idx} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {candidateList.length > 0 ? (
              candidateList.map((candidate) => (
                <tr 
                  key={candidate.id} 
                  className={`candidate-row${isSelected(candidate.id) ? ' row-selected' : ''}`}
                  onClick={() => handleCandidateClick(candidate)}
                >
                  {visibleColumnIds.map((columnId) => {
                    if (columnId === 'select') {
                      return (
                        <td key={columnId} className="list-select-cell" onClick={(e) => e.stopPropagation()}>
                          {someSelected ? (
                            <input
                              type="checkbox"
                              aria-label={`Select ${candidate.first_name} ${candidate.last_name}`}
                              checked={isSelected(candidate.id)}
                              onChange={() => toggleOne(candidate.id)}
                            />
                          ) : null}
                        </td>
                      );
                    }
                    if (columnId === 'name') {
                      return (
                        <td key={columnId}>
                          <div className="candidate-name-cell">
                            <FiUser className="candidate-icon" />
                            <strong>{candidate.first_name} {candidate.last_name}</strong>
                          </div>
                        </td>
                      );
                    }
                    if (columnId === 'email') {
                      return (
                        <td key={columnId}>
                          <span className="candidate-email-inline">
                            <FiMail /> {candidate.email}
                          </span>
                        </td>
                      );
                    }
                    if (columnId === 'phone') {
                      return (
                        <td key={columnId}>
                          {candidate.phone ? (
                            <span className="candidate-phone-inline">
                              <FiPhone /> {candidate.phone}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      );
                    }
                    if (columnId === 'current_position') {
                      return (
                        <td key={columnId}>
                          {candidate.current_job_title || candidate.job_classification_name ? (
                            <div className="candidate-position">
                              <div>
                                {candidate.current_job_title && <strong>{candidate.current_job_title}</strong>}
                                {candidate.current_job_title && candidate.job_classification_name && <span> • </span>}
                                {candidate.job_classification_name && <span className="text-muted">({candidate.job_classification_name})</span>}
                              </div>
                              {candidate.current_company && (
                                <div className="candidate-company">{candidate.current_company}</div>
                              )}
                            </div>
                          ) : (
                            '—'
                          )}
                        </td>
                      );
                    }
                    if (columnId === 'experience') {
                      return (
                        <td key={columnId}>
                          {candidate.years_of_experience ? (
                            <span className="tabular-nums">{candidate.years_of_experience} years</span>
                          ) : (
                            '—'
                          )}
                        </td>
                      );
                    }
                    if (columnId === 'resumes') {
                      return (
                        <td key={columnId}>
                          <span className="candidate-stat tabular-nums">{candidate.resume_count || 0}</span>
                        </td>
                      );
                    }
                    if (columnId === 'matches') {
                      return (
                        <td key={columnId}>
                          <span
                            className="candidate-stat tabular-nums"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/matches?candidate=${candidate.id}`);
                            }}
                            role="link"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/matches?candidate=${candidate.id}`);
                              }
                            }}
                            title="View matched jobs"
                          >
                            {candidate.match_count || 0}
                          </span>
                        </td>
                      );
                    }
                    if (columnId === 'package') {
                      const pkgActive = String(candidate.package_status || '').toLowerCase() === 'active';
                      return (
                        <td key={columnId}>
                          <span
                            className={`badge ${pkgActive ? 'badge-success' : 'badge-muted'}`}
                            title={
                              pkgActive
                                ? candidate.active_package_name || 'Active service package'
                                : 'No active service package'
                            }
                          >
                            {pkgActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      );
                    }
                    if (columnId === 'status') {
                      return (
                        <td key={columnId} onClick={(e) => e.stopPropagation()}>
                          <div className="candidate-status-cell">
                            {user?.role === 'admin' ? (
                              <>
                                <select
                                  className={`status-select status-select--${isActiveStatus(candidate.is_active) ? 'active' : 'inactive'}`}
                                  value={isActiveStatus(candidate.is_active) ? 'active' : 'inactive'}
                                  onChange={(e) => {
                                    const newStatus = e.target.value === 'active';
                                    updateStatusMutation.mutate({
                                      candidateId: candidate.id,
                                      is_active: newStatus
                                    });
                                  }}
                                  disabled={updateStatusMutation.isLoading}
                                >
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                                {candidate.assignment_status && (
                                  <span className="badge badge-info">
                                    {candidate.assignment_status}
                                  </span>
                                )}
                              </>
                            ) : (
                              candidate.assignment_status ? (
                                <span className="badge badge-info">
                                  {candidate.assignment_status}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )
                            )}
                          </div>
                        </td>
                      );
                    }
                    return null;
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(visibleColumnIds.length, 1)} className="empty-state">
                  {user?.role === 'admin' ? 'No candidates found' : 'No candidates assigned'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ListPager
        page={candidatePagination.page}
        pageCount={candidatePagination.pageCount}
        total={candidatePagination.total}
        limit={candidatePagination.limit}
        onPageChange={setCandidatePage}
      />

      {/* Select register row to onboard */}
      <Modal
        open={showRegisterSelectModal}
        onClose={closeRegisterSelectModal}
        contentClassName="add-candidate-modal add-candidate-modal--register-picker"
        ariaLabel="Onboard from Register"
      >
        <div className="modal-header">
          <h2>
            <FiUsers /> Onboard from Register
          </h2>
          <button
            type="button"
            className="btn-close-modal"
            onClick={closeRegisterSelectModal}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label htmlFor="register-picker-search">Search</label>
          <input
            id="register-picker-search"
            type="text"
            placeholder="Name, email, phone, position, service, ref…"
            value={registerPickerSearch}
            onChange={(e) => setRegisterPickerSearch(e.target.value)}
          />
        </div>
        <div className="register-picker-table-wrap">
          {registerRowsLoading && <div className="loading" style={{ padding: 24 }}>Loading register list…</div>}
          {registerRowsError && (
            <div className="error" style={{ padding: 16 }}>
              Could not load register list. Ensure you have access to Register data.
            </div>
          )}
          {!registerRowsLoading && !registerRowsError && filteredRegisterRows.length === 0 && (
            <div className="empty-state" style={{ padding: 24 }}>
              {registerPickerSearch.trim()
                ? 'No matching records.'
                : 'No register candidates available to onboard. They may already exist as candidates.'}
            </div>
          )}
          {!registerRowsLoading && !registerRowsError && filteredRegisterRows.length > 0 && (
            <table className="table" style={{ margin: 0, fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={{ width: 48 }} />
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Position</th>
                  <th>Service</th>
                  <th>Start</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegisterRows.map((r) => {
                  const fname = r.fname ?? r.first_name ?? '';
                  const lname = r.lname ?? r.last_name ?? '';
                  const name = `${fname} ${lname}`.trim() || '—';
                  const selected = String(registerPickerSelectedId) === String(r.id);
                  return (
                    <tr
                      key={r.id ?? `${r.email}-${name}`}
                      onClick={() => setRegisterPickerSelectedId(r.id)}
                      style={{
                        cursor: 'pointer',
                        background: selected
                          ? 'color-mix(in srgb, var(--primary-color) 8%, transparent)'
                          : undefined,
                      }}
                    >
                      <td>
                        <input
                          type="radio"
                          name="registerPick"
                          checked={selected}
                          onChange={() => setRegisterPickerSelectedId(r.id)}
                          aria-label={`Select ${name}`}
                        />
                      </td>
                      <td>{name}</td>
                      <td>{r.email ?? ''}</td>
                      <td>{r.phone ?? ''}</td>
                      <td>{r.position ?? ''}</td>
                      <td>{r.service ?? ''}</td>
                      <td>{r.start_date ? String(r.start_date).slice(0, 10) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={closeRegisterSelectModal}>
            Back to form
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!registerPickerSelectedId}
            onClick={handleContinueFromRegisterSelection}
          >
            Continue with selected
          </button>
        </div>
      </Modal>

      {/* Add Candidate Modal — opens directly on Add candidate */}
      <Modal
        open={showAddModal}
        onClose={closeAddModal}
        contentClassName="add-candidate-modal"
        ariaLabel={addCandidateSource === 'register' ? 'Onboard candidate' : 'Add new candidate'}
        confirmClose
      >
            <div className="modal-header">
              <h2>
                <FiUser /> {addCandidateSource === 'register' ? 'Onboard candidate' : 'Add New Candidate'}
              </h2>
              <div className="modal-header-actions">
                {addCandidateSource !== 'register' && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={openOnboardFromRegister}
                  >
                    <FiUsers /> Onboard from Register
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close-modal"
                  onClick={closeAddModal}
                  aria-label="Close"
                >
                  <FiX />
                </button>
              </div>
            </div>
            {addCandidateSource === 'register' && selectedRegisterRow && (
              <p className="add-candidate-register-banner">
                Pre-filled from register
                {selectedRegisterRow.email ? ` (${selectedRegisterRow.email})` : ''}
                {selectedRegisterRow.service ? ` · Service: ${selectedRegisterRow.service}` : ''}
                . Set a password and complete any missing fields.
                {' '}
                <button
                  type="button"
                  className="btn-link"
                  onClick={openOnboardFromRegister}
                >
                  Choose a different register record
                </button>
              </p>
            )}

            <form onSubmit={handleAddCandidate} className="add-candidate-form">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-cand-first-name">First Name *</label>
                    <input
                      id="add-cand-first-name"
                      type="text"
                      value={candidateFormData.first_name}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-cand-last-name">Last Name *</label>
                    <input
                      id="add-cand-last-name"
                      type="text"
                      value={candidateFormData.last_name}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-cand-email">Email *</label>
                    <input
                      id="add-cand-email"
                      type="email"
                      value={candidateFormData.email}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="add-cand-phone">Phone</label>
                    <input
                      id="add-cand-phone"
                      type="tel"
                      value={candidateFormData.phone}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="add-cand-password">Password * {addCandidateSource === 'register' && <span style={{ fontWeight: 400 }}>(for dashboard login)</span>}</label>
                    <input
                      id="add-cand-password"
                      type="password"
                      value={candidateFormData.password}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={candidateFormData.date_of_birth}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, date_of_birth: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Address</h3>
                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={candidateFormData.address}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, address: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={candidateFormData.city}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, city: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>State/Province</label>
                    <input
                      type="text"
                      value={candidateFormData.state}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, state: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={candidateFormData.country}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, country: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Zip/Postal Code</label>
                    <input
                      type="text"
                      value={candidateFormData.zip_code}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, zip_code: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Professional Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Job Classification</label>
                    <select
                      value={candidateFormData.job_classification || ''}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, job_classification: e.target.value })}
                    >
                      <option value="">Select a job classification</option>
                      {jobRoles
                        .filter(role => role.is_active === 1 || role.is_active === true)
                        .sort((a, b) => timestampSortValue(a) - timestampSortValue(b))
                        .map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Job Title</label>
                    <input
                      type="text"
                      value={candidateFormData.current_job_title || ''}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, current_job_title: e.target.value })}
                      placeholder="e.g., Senior Software Engineer"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Current Company</label>
                    <input
                      type="text"
                      value={candidateFormData.current_company}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, current_company: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Years of Experience</label>
                    <input
                      type="number"
                      value={candidateFormData.years_of_experience}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, years_of_experience: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Availability</label>
                    <select
                      value={candidateFormData.availability}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, availability: e.target.value })}
                    >
                      <option value="available">Available Immediately</option>
                      <option value="2-weeks">Available in 2 Weeks</option>
                      <option value="1-month">Available in 1 Month</option>
                      <option value="2-months">Available in 2 Months</option>
                      <option value="3-months">Available in 3+ Months</option>
                      <option value="not-available">Not Available</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Professional Summary</label>
                  <textarea
                    value={candidateFormData.summary}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, summary: e.target.value })}
                    rows="4"
                    placeholder="Brief professional summary..."
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Online Presence</h3>
                <div className="form-group">
                  <label>LinkedIn URL</label>
                  <input
                    type="url"
                    value={candidateFormData.linkedin_url}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Portfolio/Website URL</label>
                    <input
                      type="url"
                      value={candidateFormData.portfolio_url}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, portfolio_url: e.target.value })}
                      placeholder="https://yourportfolio.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>GitHub URL</label>
                    <input
                      type="url"
                      value={candidateFormData.github_url}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, github_url: e.target.value })}
                      placeholder="https://github.com/username"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Job Preferences</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Expected Salary Min</label>
                    <input
                      type="number"
                      value={candidateFormData.expected_salary_min}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, expected_salary_min: e.target.value })}
                      min="0"
                      placeholder="e.g., 80000"
                    />
                  </div>
                  <div className="form-group">
                    <label>Expected Salary Max</label>
                    <input
                      type="number"
                      value={candidateFormData.expected_salary_max}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, expected_salary_max: e.target.value })}
                      min="0"
                      placeholder="e.g., 120000"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Work Authorization</label>
                    <input
                      type="text"
                      value={candidateFormData.work_authorization}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, work_authorization: e.target.value })}
                      placeholder="e.g., US Citizen, H1B, Green Card"
                    />
                  </div>
                  <div className="form-group">
                    <label>Willing to Relocate</label>
                    <select
                      value={candidateFormData.willing_to_relocate ? 'yes' : 'no'}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, willing_to_relocate: e.target.value === 'yes' })}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Preferred Locations</label>
                  <input
                    type="text"
                    value={candidateFormData.preferred_locations}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, preferred_locations: e.target.value })}
                    placeholder="Comma-separated: San Francisco, CA, New York, NY"
                  />
                  <p className="form-hint">Separate multiple locations with commas</p>
                </div>
              </div>

              <div className="form-section">
                <h3>Additional Notes</h3>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={candidateFormData.additional_notes}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, additional_notes: e.target.value })}
                    rows="3"
                    placeholder="Any additional notes about the candidate..."
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Resumes (Maximum 3)</h3>
                <p className="form-hint">You can upload up to 3 resumes for this candidate. Each resume can have additional details.</p>
                {[0, 1, 2].map((index) => (
                  <div key={index} className="resume-upload-item">
                    <div className="resume-upload-header">
                      <h4>Resume {index + 1}</h4>
                      {resumeFiles[index] && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeResume(index)}
                        >
                          <FiTrash2 /> Remove
                        </button>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Resume File (PDF, DOC, DOCX)</label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleResumeFileChange(index, e.target.files[0]);
                          }
                        }}
                      />
                      {resumeFiles[index] && (
                        <p className="form-hint">Selected: {resumeFiles[index].name}</p>
                      )}
                      <p className="form-hint">
                        PDF/DOCX is sent to OCR when enabled, then AI fills profile fields
                        (skills, experience, education, etc.).
                      </p>
                    </div>
                    {resumeFiles[index] && (
                      <>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Skills (comma-separated)</label>
                            <input
                              type="text"
                              value={resumeData[index].skills}
                              onChange={(e) => handleResumeDataChange(index, 'skills', e.target.value)}
                              placeholder="JavaScript, React, Node.js"
                            />
                          </div>
                          <div className="form-group">
                            <label>Years of Experience</label>
                            <input
                              type="number"
                              value={resumeData[index].experience_years}
                              onChange={(e) => handleResumeDataChange(index, 'experience_years', e.target.value)}
                              min="0"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Education</label>
                          <input
                            type="text"
                            value={resumeData[index].education}
                            onChange={(e) => handleResumeDataChange(index, 'education', e.target.value)}
                            placeholder="Bachelor's in Computer Science"
                          />
                        </div>
                        <div className="form-group">
                          <label>Summary</label>
                          <textarea
                            value={resumeData[index].summary}
                            onChange={(e) => handleResumeDataChange(index, 'summary', e.target.value)}
                            rows="2"
                            placeholder="Brief summary of this resume..."
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {resumeFiles.filter(f => f !== null && f !== undefined).length >= 3 && (
                  <p className="form-hint" style={{ color: '#28a745', fontWeight: 'bold' }}>
                    Maximum of 3 resumes reached
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAddModal}
                >
                  Cancel
                </button>
                <LoadingButton
                  type="submit"
                  className="btn btn-success"
                  icon={FiPlus}
                  loading={createCandidateMutation.isLoading}
                  loadingLabel="Creating..."
                >
                  Create Candidate
                </LoadingButton>
              </div>
            </form>
      </Modal>

      {/* Edit Candidate Modal */}
      <Modal
        open={Boolean(showEditModal && editingCandidate)}
        onClose={() => { setShowEditModal(false); setEditingCandidate(null); resetForm(); }}
        contentClassName="add-candidate-modal"
        ariaLabel={
          editingCandidate
            ? `Edit candidate ${editingCandidate.first_name || ''} ${editingCandidate.last_name || ''}`.trim()
            : 'Edit candidate'
        }
        confirmClose
      >
            <div className="modal-header">
              <h2>
                <FiUser /> Edit Candidate: {editingCandidate?.first_name} {editingCandidate?.last_name}
              </h2>
              <div className="modal-header-actions" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {editingCandidate?.email && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    title="Send canned email template"
                    onClick={() => {
                      setEmailRecipient({
                        userId: editingCandidate.id,
                        email: editingCandidate.email,
                        firstName: editingCandidate.first_name,
                        lastName: editingCandidate.last_name,
                        displayName: `${editingCandidate.first_name || ''} ${editingCandidate.last_name || ''}`.trim(),
                      });
                      setShowSendEmail(true);
                    }}
                  >
                    <FiMail /> Email
                  </button>
                )}
                <button
                  type="button"
                  className="btn-close-modal"
                  onClick={() => { setShowEditModal(false); setEditingCandidate(null); resetForm(); }}
                  aria-label="Close"
                >
                  <FiX />
                </button>
              </div>
            </div>

            <form onSubmit={handleUpdateCandidate} className="add-candidate-form">
              <div className="form-section">
                <h3>Basic Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={candidateFormData.first_name}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={candidateFormData.last_name}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={candidateFormData.email}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={candidateFormData.phone}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, phone: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      value={candidateFormData.password}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, password: e.target.value })}
                      placeholder="Leave blank to keep current password"
                    />
                    <p className="form-hint">Leave blank to keep current password</p>
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={candidateFormData.date_of_birth}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, date_of_birth: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Address</h3>
                <div className="form-group">
                  <label>Street Address</label>
                  <input
                    type="text"
                    value={candidateFormData.address}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, address: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={candidateFormData.city}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, city: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>State/Province</label>
                    <input
                      type="text"
                      value={candidateFormData.state}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, state: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      value={candidateFormData.country}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, country: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Zip/Postal Code</label>
                    <input
                      type="text"
                      value={candidateFormData.zip_code}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, zip_code: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Professional Information</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Job Classification</label>
                    <select
                      value={candidateFormData.job_classification || ''}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, job_classification: e.target.value })}
                    >
                      <option value="">Select a job classification</option>
                      {jobRoles
                        .filter(role => role.is_active === 1 || role.is_active === true)
                        .sort((a, b) => timestampSortValue(a) - timestampSortValue(b))
                        .map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Job Title</label>
                    <input
                      type="text"
                      value={candidateFormData.current_job_title || ''}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, current_job_title: e.target.value })}
                      placeholder="e.g., Senior Software Engineer"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Current Company</label>
                    <input
                      type="text"
                      value={candidateFormData.current_company}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, current_company: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Years of Experience</label>
                    <input
                      type="number"
                      value={candidateFormData.years_of_experience}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, years_of_experience: e.target.value })}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Availability</label>
                    <select
                      value={candidateFormData.availability}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, availability: e.target.value })}
                    >
                      <option value="available">Available Immediately</option>
                      <option value="2-weeks">Available in 2 Weeks</option>
                      <option value="1-month">Available in 1 Month</option>
                      <option value="2-months">Available in 2 Months</option>
                      <option value="3-months">Available in 3+ Months</option>
                      <option value="not-available">Not Available</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Professional Summary</label>
                  <textarea
                    value={candidateFormData.summary}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, summary: e.target.value })}
                    rows="4"
                    placeholder="Brief professional summary..."
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Online Presence</h3>
                <div className="form-group">
                  <label>LinkedIn URL</label>
                  <input
                    type="url"
                    value={candidateFormData.linkedin_url}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, linkedin_url: e.target.value })}
                    placeholder="https://linkedin.com/in/username"
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Portfolio/Website URL</label>
                    <input
                      type="url"
                      value={candidateFormData.portfolio_url}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, portfolio_url: e.target.value })}
                      placeholder="https://yourportfolio.com"
                    />
                  </div>
                  <div className="form-group">
                    <label>GitHub URL</label>
                    <input
                      type="url"
                      value={candidateFormData.github_url}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, github_url: e.target.value })}
                      placeholder="https://github.com/username"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Job Preferences</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Expected Salary Min</label>
                    <input
                      type="number"
                      value={candidateFormData.expected_salary_min}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, expected_salary_min: e.target.value })}
                      min="0"
                      placeholder="e.g., 80000"
                    />
                  </div>
                  <div className="form-group">
                    <label>Expected Salary Max</label>
                    <input
                      type="number"
                      value={candidateFormData.expected_salary_max}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, expected_salary_max: e.target.value })}
                      min="0"
                      placeholder="e.g., 120000"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Work Authorization</label>
                    <input
                      type="text"
                      value={candidateFormData.work_authorization}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, work_authorization: e.target.value })}
                      placeholder="e.g., US Citizen, H1B, Green Card"
                    />
                  </div>
                  <div className="form-group">
                    <label>Willing to Relocate</label>
                    <select
                      value={candidateFormData.willing_to_relocate ? 'yes' : 'no'}
                      onChange={(e) => setCandidateFormData({ ...candidateFormData, willing_to_relocate: e.target.value === 'yes' })}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Preferred Locations</label>
                  <input
                    type="text"
                    value={candidateFormData.preferred_locations}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, preferred_locations: e.target.value })}
                    placeholder="Comma-separated: San Francisco, CA, New York, NY"
                  />
                  <p className="form-hint">Separate multiple locations with commas</p>
                </div>
              </div>

              <div className="form-section">
                <h3>Additional Notes</h3>
                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    value={candidateFormData.additional_notes}
                    onChange={(e) => setCandidateFormData({ ...candidateFormData, additional_notes: e.target.value })}
                    rows="3"
                    placeholder="Any additional notes about the candidate..."
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Resumes (Maximum 3)</h3>
                <p className="form-hint">You can update existing resumes or add new ones. Upload a new file to replace an existing resume.</p>
                {[0, 1, 2].map((index) => {
                  const existingResume = resumeData[index]?.id;
                  return (
                    <div key={index} className="resume-upload-item">
                      <div className="resume-upload-header">
                        <h4>Resume {index + 1} {existingResume && <span style={{ fontSize: '12px', color: '#666' }}>(Existing)</span>}</h4>
                        {resumeFiles[index] && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => removeResume(index)}
                          >
                            <FiTrash2 /> Remove
                          </button>
                        )}
                        {existingResume && !resumeFiles[index] && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={async () => {
                              if (await appConfirm('Are you sure you want to delete this resume?')) {
                                try {
                                  await api.delete(`/resumes/${existingResume}`);
                                  const newResumeData = [...resumeData];
                                  const newResumeFiles = [...resumeFiles];
                                  newResumeData[index] = { skills: '', experience_years: '', education: '', summary: '' };
                                  newResumeFiles[index] = null;
                                  setResumeData(newResumeData);
                                  setResumeFiles(newResumeFiles);
                                  queryClient.invalidateQueries('candidates');
                                } catch (error) {
                                  void appAlert('Failed to delete resume');
                                }
                              }
                            }}
                          >
                            <FiTrash2 /> Delete
                          </button>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Resume File (PDF, DOC, DOCX) {existingResume && <span style={{ fontSize: '12px', color: '#666' }}>- Leave empty to keep current file</span>}</label>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleResumeFileChange(index, e.target.files[0]);
                            }
                          }}
                        />
                        {resumeFiles[index] && (
                          <p className="form-hint">Selected: {resumeFiles[index].name}</p>
                        )}
                        <p className="form-hint">
                          PDF/DOCX is sent to OCR when enabled, then AI fills profile sections.
                        </p>
                      </div>
                      {(resumeFiles[index] || existingResume) && (
                        <>
                          <div className="form-row">
                            <div className="form-group">
                              <label>Skills (comma-separated)</label>
                              <input
                                type="text"
                                value={resumeData[index].skills}
                                onChange={(e) => handleResumeDataChange(index, 'skills', e.target.value)}
                                placeholder="JavaScript, React, Node.js"
                              />
                            </div>
                            <div className="form-group">
                              <label>Years of Experience</label>
                              <input
                                type="number"
                                value={resumeData[index].experience_years}
                                onChange={(e) => handleResumeDataChange(index, 'experience_years', e.target.value)}
                                min="0"
                              />
                            </div>
                          </div>
                          <div className="form-group">
                            <label>Education</label>
                            <input
                              type="text"
                              value={resumeData[index].education}
                              onChange={(e) => handleResumeDataChange(index, 'education', e.target.value)}
                              placeholder="Bachelor's in Computer Science"
                            />
                          </div>
                          <div className="form-group">
                            <label>Summary</label>
                            <textarea
                              value={resumeData[index].summary}
                              onChange={(e) => handleResumeDataChange(index, 'summary', e.target.value)}
                              rows="2"
                              placeholder="Brief summary of this resume..."
                            />
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                {resumeFiles.filter(f => f !== null && f !== undefined).length >= 3 && (
                  <p className="form-hint" style={{ color: '#28a745', fontWeight: 'bold' }}>
                    Maximum of 3 resumes reached
                  </p>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { setShowEditModal(false); setEditingCandidate(null); resetForm(); }}
                >
                  Cancel
                </button>
                <LoadingButton
                  type="submit"
                  className="btn btn-primary"
                  icon={FiSave}
                  loading={updateCandidateMutation.isLoading}
                  loadingLabel="Updating..."
                >
                  Update Candidate
                </LoadingButton>
              </div>
            </form>
      </Modal>

      <SendTemplateEmailModal
        open={showSendEmail && Boolean(emailRecipient)}
        onClose={() => {
          setShowSendEmail(false);
          setEmailRecipient(null);
        }}
        recipient={emailRecipient}
      />
    </div>
  );
};

export default Candidates;

